import {AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormBuilder, FormControl, Validators} from "@angular/forms";

import {parseISO} from 'date-fns';
import {TranslateService} from '@ngx-translate/core';

import {Vehicle} from "@commons/models/domain/Vehicle";

import {LinkViewModel} from '@data-sharing/view-models/link-view-model';
import {LinkDateRangeModel} from "@data-sharing/routes/link-view/link-date-range.model";
import {DataSharingLink} from "@data-sharing/models/data-sharing-link";
import {DataSharingHttpService} from "@data-sharing/http-services/data-sharing-http.service";

import {NewApiErrorResult} from "@shared/services-http/responses/new-api-error.result";

@Component({
  selector: 'dsm-link-view',
  templateUrl: './link-view.component.html',
  styleUrls: ['./link-view.component.less']
})
export class LinkViewComponent implements OnInit, OnChanges, AfterViewInit {

  @Input() inputLinkViewModel?: LinkViewModel;

  @Output() linkAdded = new EventEmitter<LinkViewModel>();

  isLoading = false;
  isViewMode = false;
  existingLinkDateRange?: LinkDateRangeModel;
  fullSelectedVehicles: Vehicle[] = [];
  isAlwaysValid = false;
  createNewLinkTrans = '';
  noWorkerErrorMessage = '';
  nameAlreadyUsed = '';
  formError = '';

  nameCtrl = new FormControl('', [Validators.required, Validators.min(3), Validators.max(50)]);
  vehiclesCtrl = new FormControl<number[]>([], [Validators.required, Validators.minLength(1)]);
  startDateCtrl = new FormControl(new Date(), [Validators.required]);
  endDateCtrl = new FormControl<Date | undefined>(undefined);

  form = this.formBuilder.group({
    name: this.nameCtrl,
    vehicles: this.vehiclesCtrl,
    startDate: this.startDateCtrl,
    endDate: this.endDateCtrl
  });

  get header(): string {
    return this.isViewMode ? `Link - ${this.inputLinkViewModel?.name}` : this.createNewLinkTrans;
  }

  get isFormValid(): boolean {
    return this.form.valid;
  }

  constructor(
    private formBuilder: FormBuilder,
    private dataSharingHttpService: DataSharingHttpService,
    private translateService: TranslateService,
  ) {
    this.setTranslations();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.inputLinkViewModel)
      this.initViewMode();
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
  }

  onVehiclesSelected(vehicles: Vehicle[]) {
    this.fullSelectedVehicles = vehicles;
    this.vehiclesCtrl.setValue(vehicles.map(x => x.Id));
  }

  onBackToList() {
    this.linkAdded.emit(undefined);
  }

  onDateRangeChanged(newDateRange: LinkDateRangeModel) {
    this.startDateCtrl.setValue(newDateRange?.startDate ?? null);
    this.endDateCtrl.setValue(newDateRange?.endDate ?? null);

    this.isAlwaysValid = newDateRange.endDate == null;
  }

  onSave() {
    this.isLoading = true;

    this.dataSharingHttpService.nameExists(this.nameCtrl.value!).subscribe({
      next: (nameExists) => {
        this.beforeSave(nameExists);
      },
      error: (err) => this.onSaveError(err.error)
    });
  }

  onCancel() {
    if (!this.isLoading)
      this.linkAdded.emit(undefined);
  }

  private beforeSave(nameExists: boolean) {
    if (nameExists) {
      this.formError = this.nameAlreadyUsed;
      this.isLoading = false;
      return;
    }

    if (!this.form.valid) {
      this.isLoading = false;
      return;
    }
    this.saveLink();
  }

  private saveLink() {
    const newLink = this.getNewDataSharingLink();
    this.dataSharingHttpService.post(newLink).subscribe({
        next: (savedLink) => {
          this.isLoading = false;
          this.linkAdded.emit(new LinkViewModel(savedLink, this.fullSelectedVehicles));
        },
        error: (err) => this.onSaveError(err.error)
      }
    );
  }

  private setTranslations() {
    this.translateService.get('data-sharing.create-link').subscribe(x => this.createNewLinkTrans = x);
    this.translateService.get('data-sharing.no-worker-error').subscribe(x => this.noWorkerErrorMessage = x);
    this.translateService.get('errors.name-already-used').subscribe(x => this.nameAlreadyUsed = x);
  }

  private onSaveError(err: NewApiErrorResult) {
    if (err.type) {
      switch (err.type) {
        case 'UserHasNoWorker':
          this.formError = this.noWorkerErrorMessage;
          break;
        case 'ShareLinkNameExistsForWorker':
          this.formError = this.nameAlreadyUsed;
          break;
      }
    }
    this.isLoading = false;
  }

  private getNewDataSharingLink(): DataSharingLink {
    let newLink = {} as DataSharingLink;
    newLink.name = this.nameCtrl.value!;
    newLink.vehicles = this.vehiclesCtrl.value ?? [];
    //date-fns nie obsługuje formatu ISO z literą Z na końcu chodzi niby jest tak w dokumentacji dlatego używam toISOString()
    //https://github.com/date-fns/date-fns/issues/2151
    newLink.startDate = this.startDateCtrl.value!.toISOString();
    newLink.endDate = this.isAlwaysValid ? undefined : this.endDateCtrl.value!.toISOString();

    return newLink;
  }

  private initViewMode() {
    if (!this.inputLinkViewModel)
      return;

    this.isViewMode = true;

    this.nameCtrl.setValue(this.inputLinkViewModel.link.name);
    this.vehiclesCtrl.setValue(this.inputLinkViewModel.link.vehicles);

    this.startDateCtrl.setValue(parseISO(this.inputLinkViewModel.link.startDate!));

    if (this.inputLinkViewModel?.link?.endDate)
      this.endDateCtrl.setValue(parseISO(this.inputLinkViewModel.link.endDate));

    this.existingLinkDateRange = {} as LinkDateRangeModel;
    this.existingLinkDateRange.startDate = this.inputLinkViewModel.startDate;
    this.existingLinkDateRange.endDate =
      this.inputLinkViewModel.link?.endDate ? this.inputLinkViewModel.endDate : undefined;
  }
}
